<template>
  <div class="microlearning-chapter-card">
    <div
      class="microlearning-chapter-card__content"
      :class="{ 'microlearning-chapter-card__content--readonly': readOnly }"
    >
      <button
        v-if="!readOnly"
        type="button"
        class="microlearning-chapter-card__icon-btn microlearning-chapter-card__icon-btn--edit"
        aria-label="Edit chapter"
        @click="handleCardClick"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          aria-hidden="true"
          class="microlearning-chapter-card__icon-btn-img"
        >
          <path
            d="M8.75003 2.91403L11.0836 5.24733M12.3518 3.97124C12.6602 3.66291 12.8336 3.24469 12.8336 2.80859C12.8337 2.37249 12.6605 1.95423 12.3521 1.64582C12.0437 1.33741 11.6255 1.16412 11.1893 1.16406C10.7532 1.16401 10.3349 1.3372 10.0264 1.64553L2.24057 9.43231C2.10512 9.56735 2.00495 9.73361 1.94888 9.91647L1.17823 12.4551C1.16315 12.5055 1.16201 12.5591 1.17493 12.6102C1.18785 12.6612 1.21435 12.7078 1.25162 12.745C1.28888 12.7822 1.33552 12.8086 1.38659 12.8215C1.43766 12.8343 1.49125 12.8331 1.54168 12.8179L4.08115 12.0479C4.26385 11.9924 4.43012 11.8928 4.56536 11.758L12.3518 3.97124Z"
            stroke="#344054"
            stroke-width="2"
          />
        </svg>
      </button>
      <a
        v-if="hasJumpLink"
        href="#"
        class="microlearning-chapter-card__timestamp microlearning-chapter-card__timestamp--link"
        @click.prevent="handleTimestampClick"
      >
        {{ formattedTimeRange }}
      </a>
      <span
        v-else-if="formattedTimeRange"
        class="microlearning-chapter-card__timestamp microlearning-chapter-card__timestamp--text"
      >
        {{ formattedTimeRange }}
      </span>
      <div class="microlearning-chapter-card__title-row">
        <span class="microlearning-chapter-card__bookmark" aria-hidden="true">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4 2.5C4 2.22386 4.22386 2 4.5 2H11.5C11.7761 2 12 2.22386 12 2.5V13.5L8 11L4 13.5V2.5Z"
              stroke="#7F56D9"
              stroke-width="1.2"
              stroke-linecap="round"
              stroke-linejoin="round"
              fill="none"
            />
          </svg>
        </span>
        <span class="microlearning-chapter-card__title">{{ displayTitle }}</span>
      </div>
      <p v-if="subtitleText" class="microlearning-chapter-card__subtitle">{{ subtitleText }}</p>
      <p v-if="bodyText" class="microlearning-chapter-card__description">{{ bodyText }}</p>
    </div>

    <EditStepModal
      :modelValue="showEditStepModal"
      :title="displayTitle"
      :description="editableDescription"
      :timestampRange="formattedTimeRange"
      :saving="saving"
      @input="onEditStepModalInput"
      @update:modelValue="onEditStepModalInput"
      @cancel="showEditStepModal = false"
      @save="onEditStepSave"
    />
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import EditStepModal from "@components/EditStepModal/EditStepModal.vue";

const props = defineProps({
  /** Mapped / legacy title */
  title: {
    type: String,
    default: "",
  },
  /** Mapped / legacy single description */
  description: {
    type: String,
    default: "",
  },
  timestamp_ref: {
    type: String,
    default: "",
  },
  video_jump_url: {
    type: String,
    default: "",
  },
  /** API TOC chapter fields */
  chapter_title: {
    type: String,
    default: "",
  },
  chapter_description: {
    type: String,
    default: "",
  },
  chapter_summary: {
    type: String,
    default: "",
  },
  start_time: {
    type: String,
    default: "",
  },
  end_time: {
    type: String,
    default: "",
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  saving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["timestamp-click", "card-click", "save"]);

const showEditStepModal = ref(false);

/** Trim leading zero on end segment when shaped like “01:22” → “1:22” (not “0:19”). */
function trimEndTimeDisplay(raw) {
  const t = typeof raw === "string" ? raw.trim() : "";
  if (!t) return "";
  return t.replace(/^0(\d:\d{2})$/, "$1");
}

const formattedTimeRange = computed(() => {
  const st = props.start_time?.trim();
  const et = props.end_time?.trim();
  if (st && et) {
    return `${st} - ${trimEndTimeDisplay(et)}`;
  }
  if (!props.timestamp_ref?.trim()) return "";
  const parts = props.timestamp_ref.split("-");
  if (parts.length === 2) {
    const start = parts[0].trim();
    const end = trimEndTimeDisplay(parts[1].trim());
    return `${start} - ${end}`;
  }
  return props.timestamp_ref.trim();
});

const hasJumpLink = computed(
  () => !!(formattedTimeRange.value && props.video_jump_url && String(props.video_jump_url).trim())
);

const displayTitle = computed(() => {
  const t = props.chapter_title?.trim();
  return t || props.title?.trim() || "";
});

/** Short line under title when API sends both description + summary */
const subtitleText = computed(() => {
  const desc = props.chapter_description?.trim();
  const sum = props.chapter_summary?.trim();
  if (desc && sum && desc !== sum) return desc;
  return "";
});

/** Primary body: summary first, else description, else legacy description prop */
const bodyText = computed(() => {
  const sum = props.chapter_summary?.trim();
  const desc = props.chapter_description?.trim();
  if (sum) return sum;
  if (desc) return desc;
  return props.description?.trim() || "";
});

/** Prefill EditStepModal description from the primary body text. */
const editableDescription = computed(() => bodyText.value);

function handleTimestampClick() {
  emit("timestamp-click", props.video_jump_url);
}

function handleCardClick() {
  if (props.readOnly) return;
  emit("card-click");
  showEditStepModal.value = true;
}

function onEditStepModalInput(open) {
  showEditStepModal.value = !!open;
}

function onEditStepSave(payload) {
  emit("save", {
    ...payload,
    complete: (err) => {
      if (!err) showEditStepModal.value = false;
    },
  });
}
</script>

<style scoped>
.microlearning-chapter-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.microlearning-chapter-card__timestamp {
  font-size: 14px;
  font-weight: 400;
  align-self: flex-start;
}

.microlearning-chapter-card__timestamp--link {
  color: #355ffb;
  text-decoration: underline;
  cursor: pointer;
}

.microlearning-chapter-card__timestamp--text {
  color: #355ffb;
  text-decoration: underline;
}

.microlearning-chapter-card__content {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid #eaecf0;
  background: #ffffff;
  width: 100%;
  box-sizing: border-box;
  transition: background-color 0.15s ease;
}

.microlearning-chapter-card__content:not(.microlearning-chapter-card__content--readonly) {
  padding-right: 56px;
}

.microlearning-chapter-card__content:not(.microlearning-chapter-card__content--readonly):hover {
  background-color: #f3f4ff;
}

.microlearning-chapter-card__content--readonly {
  pointer-events: none;
}

.microlearning-chapter-card__icon-btn {
  position: absolute;
  top: 50%;
  right: 12px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 36px;
  height: 36px;
  margin: 0;
  padding: 0;
  border-radius: 8px;
  cursor: pointer;
  flex-shrink: 0;
  transform: translateY(-50%);
}

.microlearning-chapter-card__icon-btn:focus-visible {
  outline: 2px solid #6941c6;
  outline-offset: 2px;
}

.microlearning-chapter-card__icon-btn-img {
  display: block;
  flex-shrink: 0;
  pointer-events: none;
}

.microlearning-chapter-card__icon-btn--edit {
  background-color: #ffffff;
  border: 1px solid #d0d5dd;
}

.microlearning-chapter-card__icon-btn--edit:hover:not(:disabled) {
  background-color: #f9fafb;
  border-color: #bfc8d4;
}

.microlearning-chapter-card__title-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.microlearning-chapter-card__bookmark {
  flex-shrink: 0;
  margin-top: 2px;
}

.microlearning-chapter-card__title {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
  margin: 0;
}

.microlearning-chapter-card__subtitle {
  font-size: 12px;
  font-weight: 400;
  line-height: 22px;
  color: #667085;
  margin: 0;
}

.microlearning-chapter-card__description {
  font-size: 12px;
  font-weight: 400;
  line-height: 22px;
  color: #475467;
  margin: 0;
  white-space: pre-line;
}

.microlearning-chapter-card__content--readonly .microlearning-chapter-card__timestamp--link {
  pointer-events: auto;
}
</style>
